import { useCallback, useId, useMemo, useState, type ChangeEvent } from 'react'
import { useIntl } from 'react-intl'

import { Badge, TextField } from '@/shared/ui'
import { Flag, Search } from '@/shared/ui/icons'

/**
 * A record's skills, from the mockups' `details/SkillsPanel.jsx`: a count beside the heading, a
 * search box that narrows the list as it is typed in, and one compact bordered row per skill.
 */
export function SkillsPanel(props: { readonly skills: readonly string[] }) {
  const intl = useIntl()
  const { skills } = props
  const [query, setQuery] = useState('')
  const headingId = useId()
  const change = useCallback((event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), [])

  const shown = useMemo(() => {
    const text = query.trim().toLocaleLowerCase(intl.locale)
    return text === '' ? skills : skills.filter((skill) => skill.toLocaleLowerCase(intl.locale).includes(text))
  }, [query, skills, intl.locale])

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h2 id={headingId} className="font-heading text-lg font-medium text-text-primary">
            {intl.formatMessage({ id: 'details.skills.title' })}
          </h2>
          <Badge>{intl.formatMessage({ id: 'details.skills.count' }, { count: skills.length })}</Badge>
        </div>
        {skills.length > 0 && (
          <search aria-label={intl.formatMessage({ id: 'details.skills.searchLabel' })} className="w-full sm:w-64">
            <TextField
              label={intl.formatMessage({ id: 'details.skills.searchLabel' })}
              hideLabel
              icon={Search}
              type="search"
              size="compact"
              value={query}
              onChange={change}
              placeholder={intl.formatMessage({ id: 'details.skills.search' })}
            />
          </search>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="rounded border border-dashed border-line px-4 py-6 text-center text-sm text-text-secondary">
          {skills.length === 0
            ? intl.formatMessage({ id: 'details.skills.empty' })
            : intl.formatMessage({ id: 'details.skills.noMatch' }, { query: query.trim() })}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {shown.map((skill, index) => (
            <li
              key={`${skill}-${String(index)}`}
              className="flex items-start gap-2.5 rounded border border-line px-3 py-2 text-sm text-text-primary"
            >
              <Flag size={14} className="mt-0.5 shrink-0 text-primary-main" />
              {skill}
            </li>
          ))}
        </ul>
      )}
      {/* Says how many skills the search left, as it changes. */}
      <output className="sr-only">
        {query.trim() === '' ? '' : intl.formatMessage({ id: 'details.skills.shown' }, { count: shown.length })}
      </output>
    </section>
  )
}
